import { useCallback, useEffect, useState } from "react";
import { Cloud, CloudOff, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { flushOfflineApiQueue } from "@/lib/api";
import { pendingOfflineCount } from "@/lib/offlineStore";

export default function OfflineStatusBanner() {
  const { user, offlineMode } = useAuth();
  const [offline, setOffline] = useState(() => !navigator.onLine || offlineMode);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(async () => {
    if (!user?.user_id) return setPending(0);
    try { setPending(await pendingOfflineCount()); } catch { setPending(0); }
  }, [user?.user_id]);

  const syncNow = useCallback(async () => {
    if (!navigator.onLine || syncing) return;
    setSyncing(true);
    try { await flushOfflineApiQueue(); } finally {
      await refreshPending();
      setSyncing(false);
    }
  }, [refreshPending, syncing]);

  useEffect(() => {
    if (!user?.user_id || !navigator.onLine) return;
    flushOfflineApiQueue().catch(() => {}).finally(refreshPending);
  }, [user?.user_id, refreshPending]);

  useEffect(() => {
    setOffline(!navigator.onLine || offlineMode);
    refreshPending();
    const onOffline = () => setOffline(true);
    const onOnline = () => { setOffline(false); syncNow(); };
    const onNetworkFailure = () => setOffline(true);
    const onNetworkSuccess = () => setOffline(!navigator.onLine);
    const onQueueChanged = () => refreshPending();
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    window.addEventListener("egs-network-offline", onNetworkFailure);
    window.addEventListener("egs-network-online", onNetworkSuccess);
    window.addEventListener("egs-offline-state", onQueueChanged);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("egs-network-offline", onNetworkFailure);
      window.removeEventListener("egs-network-online", onNetworkSuccess);
      window.removeEventListener("egs-offline-state", onQueueChanged);
    };
  }, [offlineMode, refreshPending, syncNow]);

  if (!user || (!offline && pending === 0)) return null;
  return (
    <div role="status" aria-live="polite" data-testid="offline-status-banner" className={`flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-xs ${offline ? "bg-amber-50 text-amber-950" : "bg-sky-50 text-sky-950"}`}>
      <span className="flex items-center gap-2 font-medium">{offline ? <CloudOff className="h-4 w-4" /> : <Cloud className="h-4 w-4" />}{offline ? "Offline: showing previously loaded data" : `${pending} saved change${pending === 1 ? "" : "s"} waiting to sync`}{pending > 0 && ` · ${pending} queued`}</span>
      {pending > 0 && <button type="button" onClick={syncNow} disabled={offline || syncing} className="inline-flex min-h-8 items-center gap-1.5 rounded border border-current/20 px-2 font-semibold disabled:opacity-50" data-testid="offline-sync-now"><RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Sync now"}</button>}
    </div>
  );
}